import { useCallback, useState } from "react";

import { invalidateCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentFeesService } from "../services/paymentFees.service";
import type { CreatePaymentFeePayload, PaymentFee } from "../types/paymentFees.types";

/** `missing` is a 404 — someone else already removed the row, so a refresh is the right outcome. */
export type RemoveFeeOutcome = "deleted" | "missing" | "error";

export interface CreateManyItem {
  key: string;
  payload: CreatePaymentFeePayload;
}

export interface CreateManyResult {
  key: string;
  fee: PaymentFee | null;
  error: string | null;
}

export interface RemoveInOrderResult {
  /** Rows gone, a 404 included. */
  removed: number;
  completed: boolean;
}

export interface UseFeeMutationsResult {
  isSubmitting: boolean;
  error: string | null;
  clearError: () => void;
  createFee: (payload: CreatePaymentFeePayload) => Promise<PaymentFee | null>;
  /** One POST per item, in order; a failure does not stop the rest — each row is an independent price. */
  createMany: (items: CreateManyItem[]) => Promise<CreateManyResult[]>;
  removeFee: (id: string) => Promise<RemoveFeeOutcome>;
  /** Deletes in the given order and stops at the first failure, so the caller controls what changes last. */
  removeInOrder: (ids: string[], onProgress?: (removed: number) => void) => Promise<RemoveInOrderResult>;
}

/**
 * The price book's write path — POST and DELETE are the API's only mutations. Every successful write drops
 * the corridor catalog memo: a price can be what makes a corridor available, or what takes it offline.
 */
export function useFeeMutations(): UseFeeMutationsResult {
  const { t } = useTranslations("paymentFees");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMessage = useCallback(
    (caught: unknown) =>
      HttpApiService.isAccessDeniedError(caught)
        ? t("error_no_write_permission")
        : HttpApiService.getErrorMessage(caught) || t("error_create"),
    [t],
  );

  const createFee = useCallback(
    async (payload: CreatePaymentFeePayload): Promise<PaymentFee | null> => {
      setIsSubmitting(true);
      setError(null);
      try {
        const created = await PaymentFeesService.create(payload);
        invalidateCorridors();
        return created;
      } catch (caught: unknown) {
        setError(createMessage(caught));
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [createMessage],
  );

  const createMany = useCallback(
    async (items: CreateManyItem[]): Promise<CreateManyResult[]> => {
      setIsSubmitting(true);
      setError(null);
      const results: CreateManyResult[] = [];
      try {
        for (const item of items) {
          try {
            results.push({ key: item.key, fee: await PaymentFeesService.create(item.payload), error: null });
          } catch (caught: unknown) {
            results.push({ key: item.key, fee: null, error: createMessage(caught) });
          }
        }
        if (results.some((result) => result.fee)) invalidateCorridors();
        return results;
      } finally {
        setIsSubmitting(false);
      }
    },
    [createMessage],
  );

  const removeMessage = useCallback(
    (caught: unknown) =>
      HttpApiService.isAccessDeniedError(caught)
        ? t("error_no_write_permission")
        : HttpApiService.getErrorMessage(caught) || t("error_delete"),
    [t],
  );

  const removeFee = useCallback(
    async (id: string): Promise<RemoveFeeOutcome> => {
      setIsSubmitting(true);
      setError(null);
      try {
        await PaymentFeesService.remove(id);
        invalidateCorridors();
        return "deleted";
      } catch (caught: unknown) {
        if (HttpApiService.isNotFoundError(caught)) return "missing";
        setError(removeMessage(caught));
        return "error";
      } finally {
        setIsSubmitting(false);
      }
    },
    [removeMessage],
  );

  const removeInOrder = useCallback(
    async (ids: string[], onProgress?: (removed: number) => void): Promise<RemoveInOrderResult> => {
      setIsSubmitting(true);
      setError(null);
      let removed = 0;
      try {
        for (const id of ids) {
          try {
            await PaymentFeesService.remove(id);
          } catch (caught: unknown) {
            if (!HttpApiService.isNotFoundError(caught)) {
              setError(removeMessage(caught));
              return { removed, completed: false };
            }
          }
          removed += 1;
          onProgress?.(removed);
        }
        return { removed, completed: true };
      } finally {
        if (removed > 0) invalidateCorridors();
        setIsSubmitting(false);
      }
    },
    [removeMessage],
  );

  return {
    isSubmitting,
    error,
    clearError: useCallback(() => setError(null), []),
    createFee,
    createMany,
    removeFee,
    removeInOrder,
  };
}
